import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import * as URI from 'urijs';

import { Alert, AlertSize, AlertType } from '../../components/Alert/Alert';
import { ErrorItem } from '../../components/ErrorsPresenter/ErrorsPresenter';
import { Shape2Tone } from '../../components/Shape2/Shape2';
import { TrackingParametersReplacementSelect } from '../../components/TrackingParametersBulkReplacement/TrackingParametersBulkReplacement';
import { BannerSiteLinksId, CalloutsEditorId, SiteLinkData } from '../../typings/banners';
import {
    BannerCreativeData,
    BannerSiteLinksEditorData,
    BannerWithSitelinks,
    CalloutsEditorData,
    EditedBanner,
    EditedBannerBase,
    EditedBannerCreative,
    EditedBannerTypeCpcVideo,
    EditedBannerTypeImage,
    EditedBannerTypeText,
    MassActionId,
    ProtocolValue
} from '../../typings/bannersEditState';
import { AdvType, AdvTypeClient, BannerImageForEdit, BannerImageType } from '../../typings/domain';
import { Bem } from '../Bem/Bem';
import { escapeRegexpString } from '../Escape/Escape';
import { StringHelpers } from '../StringHelpers/StringHelpers';
import { UrlHelpers } from '../UrlHelpers';

import * as keyset from './BannersEditMassActionsHelpers.i18n';

const i18n = i18nFactory(keyset);

export function getErrorForCreativeGroup(creatives: BannerCreativeData[]): ErrorItem[] {
    const hash = {};
    const hasSameSizes = creatives.some(({ width, height }) => {
        const key = `${width} ${height}`;

        if (hash[key]) {
            return true;
        }

        hash[key] = true;
        return false;
    });

    if (hasSameSizes) {
        return [
            {
                path: 'any_path',
                text: i18n('duplicate-error')
            }
        ];
    }

    return [];
}

export function getAdsToChangeText(blockName: string, adsToChangeCount: number, errors?: ErrorItem[]): React.ReactNode {
    const hasError = adsToChangeCount === 0;

    const text = i18n('add-description', {
        count: adsToChangeCount
    });
    const additionalText = i18n('warning');

    const errorsText = errors && errors.length ? errors.map(({ text: e }) => e).join('. ') : '';

    const content = errorsText ? (
        errorsText
    ) : (
        <>
            <b>{text}</b> {additionalText}
        </>
    );

    return (
        <Alert
            className={Bem.cls(blockName, 'add-description')}
            type={hasError ? AlertType.error : AlertType.warning}
            size={AlertSize.s}
            iconTone={hasError ? Shape2Tone.white : Shape2Tone.yellow}
            bordered={true}
        >
            {content}
        </Alert>
    );
}

export function getVisibleItems(selectedSourceItems: string[], item: string): string[] {
    if (selectedSourceItems.includes(item)) {
        return selectedSourceItems.filter((val: string) => val !== item);
    } else {
        return [...selectedSourceItems, item];
    }
}

export function getSitelinksFullHref(items: SiteLinkData[]): string[] {
    return items.map(item => {
        return UrlHelpers.concatHrefToProtocol(item.link || '', item.protocol);
    });
}

// Проверка на то, можем ли мы в заменить creative заменить на creativeToCompare
export function checkCreative(
    banner: EditedBanner,
    creativeToCompare: EditedBannerCreative,
    creative: EditedBannerCreative
): boolean {
    const creativeData = creative.data;
    const newData = creativeToCompare.data;

    return Boolean(
        banner.bannerType === AdvType.CPM_BANNER &&
            !banner.isSaved &&
            !banner.markedForDeletion &&
            banner.isEditAllow &&
            newData &&
            (creativeData
                ? creativeData.width === newData.width &&
                  creativeData.height === newData.height &&
                  creativeData.isAdaptive === newData.isAdaptive
                : true)
    );
}

export function getSitelinksText(items: SiteLinkData[]): string[] {
    return items.map(item => {
        return item.title;
    });
}

export function getSitelinksDescr(items: SiteLinkData[]): string[] {
    return items.map(item => {
        return item.descr;
    });
}

export function getDefaultMassActionsCalloutsState(): Record<CalloutsEditorId, CalloutsEditorData> {
    return {
        [MassActionId.bannersEdit]: {
            text: '',
            selected: [],
            isAddingCallouts: false,
            isDeletingCallouts: false
        }
    };
}

export function getAllVisibleBannerSiteLinksUrls(
    banner: BannerWithSitelinks,
    siteLinksData: BannerSiteLinksEditorData
): string[] {
    const isOpen = banner.sitelinks.isOpen;

    if (isOpen) {
        return getSitelinksFullHref(siteLinksData.items);
    }

    return getSitelinksFullHref(banner.sitelinks.items);
}

export function getAllVisibleBannerSiteLinksText(
    banner: EditedBannerTypeText,
    siteLinksData: BannerSiteLinksEditorData
): string[] {
    const isOpen = banner.sitelinks.isOpen;

    if (isOpen) {
        return getSitelinksText(siteLinksData.items);
    }

    return getSitelinksText(banner.sitelinks.items);
}

export function getAllVisibleBannerSiteLinksDescr(
    banner: EditedBannerTypeText,
    siteLinksData: BannerSiteLinksEditorData
): string[] {
    const isOpen = banner.sitelinks.isOpen;

    if (isOpen) {
        return getSitelinksDescr(siteLinksData.items);
    }

    return getSitelinksDescr(banner.sitelinks.items);
}

export function getNewBannersSiteLinks(
    siteLinksData: BannerSiteLinksEditorData,
    banner: BannerWithSitelinks,
    selectedUrls: string[],
    newHref: string,
    newProtocol: string
): SiteLinkData[] {
    return siteLinksData.items.map(
        (item: SiteLinkData, index: number): SiteLinkData => {
            let currSiteLink: SiteLinkData = banner.sitelinks.items[index] || {};
            let curHref: string;

            if (banner.sitelinks.isOpen) {
                curHref = item.protocol + item.link;
                currSiteLink = item;
            } else {
                curHref = currSiteLink.protocol + currSiteLink.link;
            }

            if (selectedUrls.includes(curHref) || (item.link === '' && selectedUrls.includes(item.link))) {
                return {
                    ...currSiteLink,
                    protocol: newProtocol as ProtocolValue,
                    link: newHref
                };
            }

            return item;
        }
    );
}

export function getNewBannersSiteLinksForPartLinkReplace(
    siteLinksData: BannerSiteLinksEditorData,
    banner: EditedBannerTypeText,
    newPartUrl: string,
    targetPartUrl: string,
    urlPart: UrlPart[]
): SiteLinkData[] {
    const isAvailable = isBannerEditMassActionsAvailable(banner);

    if (!isAvailable) {
        return siteLinksData.items;
    }

    return banner.sitelinks.items.map(
        (item: SiteLinkData, index: number): SiteLinkData => {
            let siteLink = item;

            if (banner.sitelinks.isOpen) {
                siteLink = siteLinksData.items[index];
            }

            return {
                ...siteLink,
                link: replaceUrlPart(siteLink.link || '', {
                    from: targetPartUrl,
                    to: newPartUrl,
                    urlPart
                })
            };
        }
    );
}

export function isTextReplaceMassActionsAvailable(currentBanner: EditedBanner): currentBanner is EditedBannerTypeText {
    const allowedTypes = [AdvType.TEXT, AdvTypeClient.TEXT_MOBILE];

    return allowedTypes.includes(currentBanner.bannerType);
}

export type AvailableForMassEditBanner = EditedBannerTypeText | EditedBannerTypeImage | EditedBannerTypeCpcVideo;

export function isBannerTextMassActionsAvailable(
    currentBanner: EditedBannerBase
): currentBanner is EditedBannerTypeText {
    const allowedTypes = [AdvType.TEXT, AdvTypeClient.TEXT_MOBILE];

    return allowedTypes.includes(currentBanner.bannerType) && currentBanner.isEditAllow;
}

export function isIdForMassAction(bannerId: BannerSiteLinksId): boolean {
    return MassActionId.bannersEdit === bannerId;
}

export function isBannerPriceMassActionsAvailable(
    currentBanner: EditedBannerBase
): currentBanner is EditedBannerTypeText {
    const allowedTypes = [AdvType.TEXT, AdvTypeClient.TEXT_MOBILE];

    return allowedTypes.includes(currentBanner.bannerType) && currentBanner.isEditAllow;
}

export function isBannerEditMassActionsCalloutsAvailable(
    currentBanner: EditedBannerBase
): currentBanner is EditedBannerTypeText {
    const allowedTypes = [AdvType.TEXT, AdvTypeClient.TEXT_MOBILE];

    return allowedTypes.includes(currentBanner.bannerType) && currentBanner.isEditAllow;
}

export function getNewBannersSiteLinksForTrackingParametersReplace(
    siteLinksData: BannerSiteLinksEditorData,
    banner: EditedBannerTypeText,
    newParameters: string,
    targetParameters: string,
    keyOrValueReplacement: TrackingParametersReplacementSelect
): SiteLinkData[] {
    const isAvailable = isBannerEditMassActionsAvailable(banner);

    if (!isAvailable) {
        return siteLinksData.items;
    }

    return banner.sitelinks.items.map(
        (item: SiteLinkData, index: number): SiteLinkData => {
            let siteLink = item;

            if (banner.sitelinks.isOpen) {
                siteLink = siteLinksData.items[index];
            }

            const isNeedToChange = hrefIncludesForTrackingParameterReplacement(
                siteLink.link || '',
                targetParameters,
                keyOrValueReplacement
            );

            if (isNeedToChange) {
                return {
                    ...siteLink,
                    link: replaceTrackingParameters(siteLink.link || '', {
                        from: targetParameters,
                        to: newParameters,
                        keyOrValueReplacement
                    })
                };
            } else {
                return siteLink;
            }
        }
    );
}

export function isBannerEditMassActionsAvailable(
    currentBanner: EditedBannerBase
): currentBanner is AvailableForMassEditBanner {
    const allowedTypes = new Set([
        AdvType.TEXT,
        AdvTypeClient.TEXT_MOBILE,
        AdvType.IMAGE_AD,
        AdvType.CPC_VIDEO,
        AdvType.CPM_VIDEO,
        AdvType.MCBANNER,
        AdvType.CPM_BANNER,
        AdvType.CPM_AUDIO,
        AdvType.CONTENT_PROMOTION_VIDEO,
        AdvType.CONTENT_PROMOTION_COLLECTION
    ]);

    return (
        allowedTypes.has(currentBanner.bannerType) &&
        currentBanner.isEditAllow &&
        currentBanner.markedForDeletion === false
    );
}

export function isTextBannerEditMassActionsAvailable(
    currentBanner: EditedBannerBase
): currentBanner is AvailableForMassEditBanner {
    const allowedTypes = new Set([AdvType.TEXT, AdvTypeClient.TEXT_MOBILE]);

    return allowedTypes.has(currentBanner.bannerType) && currentBanner.isEditAllow;
}

export function isVideoBannerEditMassActionsAvailable(currentBanner: EditedBanner, bannerType: AdvType): boolean {
    return (
        currentBanner.bannerType === bannerType &&
        !currentBanner.readOnly &&
        currentBanner.isEditAllow &&
        !currentBanner.markedForDeletion
    );
}

export function siteLinksIncludes(siteLinks: SiteLinkData[], targetUrl: string, urlPart: UrlPart[]): boolean {
    return siteLinks.some(item => {
        const parsedUrl = URI.parse(item.link || '');
        let flag = false;

        if (urlPart.includes(UrlPart.DOMAIN_AND_PATH)) {
            if (parsedUrl.hostname !== undefined) {
                flag = StringHelpers.indexOfSubstring(parsedUrl.hostname, targetUrl) !== -1;
                if (flag) {
                    return flag;
                }
            }

            if (parsedUrl.path !== undefined) {
                flag = StringHelpers.indexOfSubstring(parsedUrl.path, targetUrl) !== -1;
                if (flag) {
                    return flag;
                }
            }
        }

        if (urlPart.includes(UrlPart.PARAMS)) {
            if (parsedUrl.query !== null && parsedUrl.query !== undefined) {
                flag = StringHelpers.indexOfSubstring(parsedUrl.query, targetUrl) !== -1;
                if (flag) {
                    return flag;
                }
            }
        }

        return flag;
    });
}

export enum UrlPart {
    DOMAIN_AND_PATH = 'DOMAIN_AND_PATH',
    PARAMS = 'PARAMS'
}

interface ReplaceUrlPartParams {
    from: string;
    to: string;
    urlPart: UrlPart[];
}

export function replaceUrlPart(urlStr: string, params: ReplaceUrlPartParams): string {
    if (!UrlHelpers.isUrl(urlStr) || params.from.length === 0) {
        return urlStr;
    }

    if (params.urlPart.length === 0) {
        throw new Error('where должен быть непустым');
    }

    // Так как нам нужно изменять и в домене, и в пути, то нам ок, что библиотека парсит без протокола и что для урлов без протокола хост в path
    // URI.parse('ya.ru') -> { path: 'ya.ru', ...} // host - undefined
    const parsedUrl = URI.parse(urlStr);

    const replaceByParams = (str: string): string => {
        const regexp = new RegExp(escapeRegexpString(params.from), 'gi');

        return str.replace(regexp, params.to);
    };

    if (params.urlPart.includes(UrlPart.DOMAIN_AND_PATH)) {
        if (parsedUrl.hostname !== undefined) {
            parsedUrl.hostname = replaceByParams(parsedUrl.hostname);
        }

        if (parsedUrl.path !== undefined) {
            parsedUrl.path = replaceByParams(parsedUrl.path);
        }
    }

    if (params.urlPart.includes(UrlPart.PARAMS)) {
        if (parsedUrl.query !== null && parsedUrl.query !== undefined) {
            parsedUrl.query = replaceByParams(parsedUrl.query);
        }
    }

    return URI.build(parsedUrl);
}

export function hrefTurbolandingIncludesForTrackingParameterReplacement(
    href: string,
    targetParameter: string,
    keyOrValueReplacement: TrackingParametersReplacementSelect
): boolean {
    const mockLink = 'MOCK_LINK.ru?';

    return hrefIncludesForTrackingParameterReplacement(mockLink + href, targetParameter, keyOrValueReplacement);
}

export function hrefIncludesForTrackingParameterReplacement(
    href: string,
    targetParameter: string,
    keyOrValueReplacement: TrackingParametersReplacementSelect
): boolean {
    const parsedUrl = URI.parse(href);
    const parsedQuery = new URLSearchParams(parsedUrl.query);
    let flag = false;

    const lowCaseTargetParameter = targetParameter.toLowerCase();

    parsedQuery.forEach((value, key) => {
        const lowCaseValue = value.toLowerCase();
        const lowCaseKey = key.toLowerCase();

        if (
            (lowCaseKey === lowCaseTargetParameter &&
                keyOrValueReplacement === TrackingParametersReplacementSelect.KEY_REPLACEMENT) ||
            (lowCaseValue === lowCaseTargetParameter &&
                keyOrValueReplacement === TrackingParametersReplacementSelect.VALUE_REPLACEMENT)
        ) {
            flag = true;
        }
    });

    return flag;
}

export function siteLinksIncludesForTrackingParameterReplacement(
    siteLinks: SiteLinkData[],
    targetParameter: string,
    keyOrValueReplacement: TrackingParametersReplacementSelect
): boolean {
    return siteLinks.some(item => {
        return hrefIncludesForTrackingParameterReplacement(item.link || '', targetParameter, keyOrValueReplacement);
    });
}

interface ReplaceTrackingParametersParams {
    from: string;
    to: string;
    keyOrValueReplacement: TrackingParametersReplacementSelect;
}

export function replaceTurboTrackingParameters(urlStr: string, params: ReplaceTrackingParametersParams): string {
    if (urlStr === '') {
        return urlStr;
    }
    // Добавляем мок ссылки для обработки, так как подразумевается, что у турбостраницы будут только
    // гет параметры, без знака ?
    const mockLink = 'MOCK_LINK.ru?';
    urlStr = mockLink + urlStr;

    const result = replaceTrackingParameters(urlStr, params);

    return result.replace(mockLink.toLowerCase(), '').replace('?', '');
}

export function replaceTrackingParameters(urlStr: string, params: ReplaceTrackingParametersParams): string {
    const lowCaseUrlStr = urlStr.toLowerCase();
    const lowCaseFrom = params.from.toLowerCase();
    const lowCaseTo = params.to.toLowerCase();

    if (!UrlHelpers.isUrl(lowCaseUrlStr)) {
        return lowCaseUrlStr;
    }

    // Так как нам нужно изменять и в домене, и в пути, то нам ок, что библиотека парсит без протокола и что для урлов без протокола хост в path
    // URI.parse('ya.ru') -> { path: 'ya.ru', ...} // host - undefined
    const parsedUrl = URI.parse(lowCaseUrlStr);
    const parsedQuery = new URLSearchParams(parsedUrl.query);

    const getStringQuery = (query: URLSearchParams): string => {
        let str = '';
        const duplicateList: string[] = [];

        parsedQuery.forEach((value, key) => {
            if (key && !duplicateList.includes(key)) {
                str += '&' + key + '=' + query.get(key);

                // Избавляемся от дубликатов в параметрах
                duplicateList.push(key);
            }
        });

        return str.slice(1);
    };

    if (params.keyOrValueReplacement === TrackingParametersReplacementSelect.KEY_REPLACEMENT) {
        parsedQuery.forEach((value, key) => {
            if (key === lowCaseFrom) {
                const savedValue = parsedQuery.get(key) || '';
                parsedQuery.delete(key);
                parsedQuery.append(lowCaseTo, savedValue);
            }
        });
    }

    if (params.keyOrValueReplacement === TrackingParametersReplacementSelect.VALUE_REPLACEMENT) {
        parsedQuery.forEach((value, key) => {
            if (value === lowCaseFrom) {
                parsedQuery.set(key, lowCaseTo);
            }
        });
    }

    parsedUrl.query = getStringQuery(parsedQuery);

    return URI.build(parsedUrl);
}

export const getMassActionsCheckBoxState = (newCheckBoxState: boolean[], oldCheckBoxState: boolean[]): boolean[] => {
    const isAllCheckBoxesDisabled = newCheckBoxState.every(item => !item);
    const newState = newCheckBoxState.slice(0);

    if (isAllCheckBoxesDisabled) {
        const oldCheckBoxStateLength = oldCheckBoxState.length - 1;

        for (let i = 0; i <= oldCheckBoxStateLength; i++) {
            const item = oldCheckBoxState[i];

            if (item === true && i !== oldCheckBoxStateLength) {
                newState[i + 1] = true;
            }

            if (item === true && i === oldCheckBoxStateLength) {
                newState[0] = true;
            }
        }
    }

    return newState;
};

export enum BannersEditCreativeImageSetKeyType {
    NORMAL = 'NORMAL',
    ADAPTIVE = 'ADAPTIVE'
}

export const getImageSetKey = (
    width: number,
    height: number,
    adaptiveType: BannersEditCreativeImageSetKeyType = BannersEditCreativeImageSetKeyType.NORMAL,
    creativeType?: BannerImageType
): string => `${width}|${height}|${creativeType}|${adaptiveType}`;

export const validateImageCreativeItemsBySizeDuplicates = (creative: BannerImageForEdit[]): boolean => {
    const set = new Set();

    for (let i = 0; i < creative.length; i++) {
        const { width, height, isAdaptive, type: creativeType } = creative[i];

        const adaptiveType = isAdaptive
            ? BannersEditCreativeImageSetKeyType.ADAPTIVE
            : BannersEditCreativeImageSetKeyType.NORMAL;

        const key = getImageSetKey(width, height, adaptiveType, creativeType);

        if (set.has(key)) {
            return false;
        } else {
            set.add(key);
        }
    }

    return true;
};

export const convertImageCreativeToSet = (creative: BannerImageForEdit[]): Record<string, BannerImageForEdit> => {
    return creative.reduce(
        (acc: Record<string, BannerImageForEdit>, currCreative: BannerImageForEdit) => {
            const { width, height, isAdaptive, type: creativeType } = currCreative;

            const adaptiveType = isAdaptive
                ? BannersEditCreativeImageSetKeyType.ADAPTIVE
                : BannersEditCreativeImageSetKeyType.NORMAL;

            acc[getImageSetKey(width, height, adaptiveType, creativeType)] = currCreative;

            return acc;
        },
        {} as Record<string, BannerImageForEdit>
    );
};

export const getItemFromImageSet = (
    width: number,
    height: number,
    adaptiveType: BannersEditCreativeImageSetKeyType,
    imageCreativeSet: Record<string, BannerImageForEdit>,
    creativeType?: BannerImageType
): BannerImageForEdit | undefined => imageCreativeSet[getImageSetKey(width, height, adaptiveType, creativeType)];
